import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode
} from "react";
import { useLocation } from "react-router-dom";
import { formatTitle } from "config/page-titles";
import { useDocumentTitle } from "hooks/use-document-title";

interface PageTitleOverride {
  pathname: string;
  formattedTitle: string;
}

interface PageTitleContextValue {
  override: PageTitleOverride | null;
  /**
   * Set an entity-specific browser-tab title for the CURRENT page (Phase 2).
   * Prefer the `usePageTitle` hook over calling this directly.
   */
  setPageTitle: (page: string) => void;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  override: null,
  setPageTitle: () => {}
});

/** Access the page-title override channel (consumed by the title hooks). */
export const usePageTitleOverride = (): PageTitleContextValue =>
  useContext(PageTitleContext);

/** Headless component that owns the `document.title` write. Renders nothing. */
function DocumentTitle(): null {
  useDocumentTitle();
  return null;
}

/**
 * Drives the dynamic browser-tab title for the main app. Mount ONCE inside each
 * `<Router>` (it relies on `useLocation`). It writes the static title from
 * `config/page-titles` for the current route and lets detail pages override it
 * with an entity-specific title via `usePageTitle`.
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [override, setOverride] = useState<PageTitleOverride | null>(null);

  // Drop any page-set override when the route changes so it can't go stale.
  useEffect(() => {
    setOverride(null);
  }, [pathname]);

  const setPageTitle = useCallback(
    (page: string) =>
      setOverride({ pathname, formattedTitle: formatTitle(page) }),
    [pathname]
  );

  const value = useMemo(
    () => ({ override, setPageTitle }),
    [override, setPageTitle]
  );

  return (
    <PageTitleContext.Provider value={value}>
      <DocumentTitle />
      {children}
    </PageTitleContext.Provider>
  );
}

export default PageTitleProvider;
